import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { AUTH_TOKEN_KEY, EV_PROFILE_UPDATED } from "@/lib/constants";

/**
 * Entitlements = { tier, features[], feature_keys[], limits{}, is_legacy }
 *
 * Backed by GET /api/pricing/entitlements. Consumers do:
 *   const { has, limit, tier } = useEntitlements();
 *   if (!has("executive_roadmap")) show upgrade CTA
 *   const cap = limit("chat_messages")  // null = unlimited
 */
const Ctx = createContext(null);

const DEFAULT_FREE = {
  tier: "free",
  is_legacy: false,
  features: [],
  feature_keys: [],
  limits: {},
};

export function EntitlementsProvider({ children }) {
  const [ent, setEnt] = useState(DEFAULT_FREE);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!t) { setEnt(DEFAULT_FREE); setLoading(false); return; }
    try {
      const { data } = await api.get("/pricing/entitlements");
      setEnt(data);
    } catch (_) { /* not logged in / boot race */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-fetch when the auth token OR profile changes (e.g. after upgrade)
  useEffect(() => {
    const onProfileUpdated = () => refresh();
    const onStorage = (e) => { if (e.key === AUTH_TOKEN_KEY) refresh(); };
    window.addEventListener(EV_PROFILE_UPDATED, onProfileUpdated);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EV_PROFILE_UPDATED, onProfileUpdated);
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  const value = useMemo(() => ({
    ...ent,
    loading,
    refresh,
    has: (key) => Array.isArray(ent.feature_keys) && ent.feature_keys.includes(key),
    limit: (action) => {
      const v = (ent.limits || {})[action];
      if (v === undefined) return 0;         // action unknown → block by default
      if (v === -1 || v === null) return null; // unlimited
      return v;
    },
    isPaid: ent.tier === "pro" || ent.tier === "premium",
    isPremium: ent.tier === "premium",
  }), [ent, loading, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useEntitlements = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useEntitlements must be used within EntitlementsProvider");
  return v;
};
